import type {
  AgentFlowAccountDTO,
  AgentFlowActionWebhookConfigDTO,
  AgentFlowDTO,
  AgentFlowHandoffConfigDTO,
  ConnectChannelSenderRequest,
  ConnectChannelSenderResponse,
  ConnectTwilioNumberRequest,
  ConnectTwilioNumberResponse,
  ConnectTwilioVoiceNumberResponse,
  CreateFlowChannelBindingRequest,
  CreateFlowChannelBindingResponse,
  FlowChannelBindingDTO,
  InitiateOutboundRequest,
  InitiateOutboundResponse,
  ListFlowChannelBindingsResponse,
  GetWhatsAppTemplateResponse,
  ListApprovedWhatsAppTemplatesResponse,
  ListTwilioChannelSendersResponse,
  ListTwilioNumbersResponse,
  RegisterWhatsAppTemplateRequest,
  RotateWebhookSecretResponse,
  TwilioChannelSenderDTO,
  TwilioNumberDTO,
  WhatsAppTemplateDTO,
  AcceleratorInstallationDTO,
  InstallDialerRequest,
  InstallDialerResponse,
  ListAcceleratorsResponse,
  ListDialerNumbersResponse,
  ListInstallationsResponse,
  DialerCallDTO,
  PlaceDialerCallRequest,
  PlaceDialerCallResponse,
  ListDialerCallsResponse,
  ListDialerOutboundNumbersResponse,
  DialerSettingsDTO,
  UpdateDialerSettingsRequest,
  ApiCredentialDTO,
  CreateApiCredentialRequest,
  CreateApiCredentialResponse,
  ListApiCredentialsResponse,
  AnalyticsResponse,
  AnalyticsWindowDays,
  ApiError,
  AuthConfigResponse,
  AuthResponse,
  BindInboundNumberRequest,
  CloneTemplateRequest,
  ConnectTwilioRequest,
  ConsentRecordDTO,
  ConversationListItemDTO,
  ConversationChannel,
  ConversationReplayDTO,
  CostInsight,
  CreateAgentFlowRequest,
  GenerateAgentFlowRequest,
  GenerateAgentFlowResponse,
  GenerateExampleCustomerRequest,
  GenerateExampleCustomerResponse,
  AgentFlowSlotDTO,
  ListAgentFlowSlotsResponse,
  CreateAgentFlowSlotRequest,
  UpdateAgentFlowSlotRequest,
  DeleteAgentFlowSlotResponse,
  ClarifyAgentFlowRequest,
  ClarifyAgentFlowResponse,
  DeleteAgentFlowResponse,
  DeliverabilityInsight,
  EngagementInsight,
  ErrorLogsSummary,
  FlowVersionDTO,
  HealthInsight,
  InsightsOverview,
  IntegrationDTO,
  ListConversationsResponse,
  ListErrorLogsQuery,
  ListErrorLogsResponse,
  ListMessagesQuery,
  ListMessagesResponse,
  ListPhoneNumbersResponse,
  ListTemplatesResponse,
  ListToolsResponse,
  MessagesSummary,
  PendingAppointmentDTO,
  PhoneNumbersSummary,
  PublishFlowResponse,
  RecordConsentRequest,
  ListTwilioConnectionsResponse,
  LoginRequest,
  MeResponse,
  SandboxTurnRequest,
  SandboxTurnResponse,
  SendTestActionEventResponse,
  SignupRequest,
  Tenant,
  TwilioConnection,
  TwilioInboundNumberDTO,
  UpdateAgentFlowAccountRequest,
  UpdateAgentFlowActionWebhookRequest,
  UpdateAgentFlowHandoffRequest,
  WorkspaceHandoffDTO,
  ConnectionHandoffDTO,
  UpdateConnectionHandoffRequest,
  UpdateAgentFlowRequest,
  UpdatePreferencesRequest,
  UpdatePreferencesResponse,
  UpdateTenantBrandingRequest,
  UpdateTwilioConnectionRequest,
  CreateKnowledgeEntryRequest,
  UpdateKnowledgeEntryRequest,
  KnowledgeEntryDTO,
  ListKnowledgeEntriesResponse,
  ListKnowledgeTagsResponse,
  ListKnowledgeDocumentsResponse,
  UpdateKnowledgeDocumentResponse,
  UploadKnowledgeDocumentResponse,
} from "@tw-pulse/shared";

export type {
  AgentFlowAccountDTO,
  AgentFlowDTO,
  FlowVersionDTO,
  TwilioInboundNumberDTO,
  KnowledgeEntryDTO,
  KnowledgeDocumentDTO,
} from "@tw-pulse/shared";

const BASE_URL =
  (import.meta.env.VITE_API_BASE_URL as string | undefined)?.replace(/\/$/, "") ?? "";

const TOKEN_KEY = "tw-pulse.token";

export function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

export function setToken(token: string | null): void {
  if (token) localStorage.setItem(TOKEN_KEY, token);
  else localStorage.removeItem(TOKEN_KEY);
}

/**
 * Absolute URL helper for full-page navigations (e.g. the Google OAuth start
 * endpoint, where the browser must follow a 302 from the API).
 */
export function apiUrl(path: string): string {
  return `${BASE_URL}${path}`;
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers = new Headers(init.headers);
  // Callers that send non-JSON bodies (e.g. raw file uploads) set their
  // own content-type; everything else defaults to JSON.
  if (!headers.has("content-type")) headers.set("content-type", "application/json");
  const token = getToken();
  if (token) headers.set("authorization", `Bearer ${token}`);

  const res = await fetch(`${BASE_URL}${path}`, { ...init, headers });
  const text = await res.text();
  const json = text ? (JSON.parse(text) as unknown) : undefined;

  if (!res.ok) {
    const apiErr = json as ApiError | undefined;
    const code = apiErr?.error?.code ?? "request_failed";
    const message = apiErr?.error?.message ?? `HTTP ${res.status}`;
    throw new ApiClientError(res.status, code, message, apiErr?.error?.details);
  }
  return json as T;
}

export class ApiClientError extends Error {
  readonly status: number;
  readonly code: string;
  /**
   * Structured detail payload from the API. For compiler errors this
   * includes which action failed and which keys are unrecognized / missing
   * — useful for surfacing actionable error messages in the UI.
   */
  readonly details?: unknown;
  constructor(status: number, code: string, message: string, details?: unknown) {
    super(message);
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

export const api = {
  config: () => request<AuthConfigResponse>("/v1/auth/config"),

  signup: (body: SignupRequest) =>
    request<AuthResponse>("/v1/auth/signup", {
      method: "POST",
      body: JSON.stringify(body),
    }),
  login: (body: LoginRequest) =>
    request<AuthResponse>("/v1/auth/login", {
      method: "POST",
      body: JSON.stringify(body),
    }),

  me: () => request<MeResponse>("/v1/users/me"),

  users: {
    updatePreferences: (body: UpdatePreferencesRequest) =>
      request<UpdatePreferencesResponse>("/v1/users/me/preferences", {
        method: "PATCH",
        body: JSON.stringify(body),
      }),
  },

  twilio: {
    connect: (body: ConnectTwilioRequest) =>
      request<TwilioConnection>("/v1/twilio/connections", {
        method: "POST",
        body: JSON.stringify(body),
      }),
    list: () => request<ListTwilioConnectionsResponse>("/v1/twilio/connections"),
    update: (id: string, body: UpdateTwilioConnectionRequest) =>
      request<TwilioConnection>(`/v1/twilio/connections/${id}`, {
        method: "PATCH",
        body: JSON.stringify(body),
      }),
    delete: (id: string) => request<void>(`/v1/twilio/connections/${id}`, { method: "DELETE" }),
    sync: (id: string) =>
      request<{ ok: boolean }>(`/v1/twilio/connections/${id}/sync`, { method: "POST" }),
  },

  workspace: {
    getHandoff: () => request<WorkspaceHandoffDTO>("/v1/workspace/handoff"),
    updateDefaultHandoff: (body: UpdateAgentFlowHandoffRequest) =>
      request<WorkspaceHandoffDTO>("/v1/workspace/handoff/default", {
        method: "PATCH",
        body: JSON.stringify(body),
      }),
    updateConnectionHandoff: (connectionId: string, body: UpdateConnectionHandoffRequest) =>
      request<ConnectionHandoffDTO>(`/v1/workspace/connections/${connectionId}/handoff`, {
        method: "PATCH",
        body: JSON.stringify(body),
      }),
  },

  accelerators: {
    list: () => request<ListAcceleratorsResponse>("/v1/accelerators"),
    listInstallations: () => request<ListInstallationsResponse>("/v1/accelerators/installations"),
    getInstallation: (id: string) =>
      request<AcceleratorInstallationDTO>(`/v1/accelerators/installations/${id}`),
    dialerNumbers: (connectionId: string) =>
      request<ListDialerNumbersResponse>(
        `/v1/accelerators/dialer/numbers?connectionId=${encodeURIComponent(connectionId)}`,
      ),
    installDialer: (body: InstallDialerRequest) =>
      request<InstallDialerResponse>("/v1/accelerators/dialer/install", {
        method: "POST",
        body: JSON.stringify(body),
      }),
    uninstall: (id: string) =>
      request<{ id: string }>(`/v1/accelerators/installations/${id}`, { method: "DELETE" }),
  },

  dialer: {
    listNumbers: () => request<ListDialerOutboundNumbersResponse>("/v1/dialer/numbers"),
    placeCall: (connectionId: string, phoneNumberSid: string, body: PlaceDialerCallRequest) =>
      request<PlaceDialerCallResponse>(
        `/v1/dialer/connections/${connectionId}/numbers/${encodeURIComponent(phoneNumberSid)}/calls`,
        { method: "POST", body: JSON.stringify(body) },
      ),
    listCalls: () => request<ListDialerCallsResponse>("/v1/dialer/calls"),
    getCall: (id: string) => request<DialerCallDTO>(`/v1/dialer/calls/${id}`),
    getSettings: () => request<DialerSettingsDTO>("/v1/dialer/settings"),
    updateSettings: (body: UpdateDialerSettingsRequest) =>
      request<DialerSettingsDTO>("/v1/dialer/settings", {
        method: "PATCH",
        body: JSON.stringify(body),
      }),
  },

  apiCredentials: {
    list: () => request<ListApiCredentialsResponse>("/v1/api-credentials"),
    create: (body: CreateApiCredentialRequest) =>
      request<CreateApiCredentialResponse>("/v1/api-credentials", {
        method: "POST",
        body: JSON.stringify(body),
      }),
    revoke: (id: string) =>
      request<ApiCredentialDTO>(`/v1/api-credentials/${id}/revoke`, { method: "POST" }),
  },

  insights: {
    overview: (connectionIds?: string[]) =>
      request<InsightsOverview>(`/v1/insights/overview${scopeQuery(connectionIds)}`),
    deliverability: (connectionIds?: string[]) =>
      request<DeliverabilityInsight>(`/v1/insights/deliverability${scopeQuery(connectionIds)}`),
    health: (connectionIds?: string[]) =>
      request<HealthInsight>(`/v1/insights/health${scopeQuery(connectionIds)}`),
    cost: (connectionIds?: string[]) =>
      request<CostInsight>(`/v1/insights/cost${scopeQuery(connectionIds)}`),
    engagement: (connectionIds?: string[]) =>
      request<EngagementInsight>(`/v1/insights/engagement${scopeQuery(connectionIds)}`),
  },

  phoneNumbers: {
    list: (connectionIds?: string[], limit = 200) =>
      request<ListPhoneNumbersResponse>(
        `/v1/phone-numbers?limit=${limit}${scopeSuffix(connectionIds)}`,
      ),
    summary: (connectionIds?: string[]) =>
      request<PhoneNumbersSummary>(`/v1/phone-numbers/summary${scopeQuery(connectionIds)}`),
  },

  messages: {
    list: (connectionIds?: string[], query: ListMessagesQuery = {}) => {
      const params = new URLSearchParams();
      if (connectionIds && connectionIds.length > 0) {
        params.set("connectionIds", connectionIds.join(","));
      }
      if (query.status) params.set("status", query.status);
      if (query.search) params.set("search", query.search);
      if (query.limit !== undefined) params.set("limit", String(query.limit));
      if (query.cursor) params.set("cursor", query.cursor);
      const qs = params.toString();
      return request<ListMessagesResponse>(`/v1/messages${qs ? `?${qs}` : ""}`);
    },
    summary: (connectionIds?: string[]) =>
      request<MessagesSummary>(`/v1/messages/summary${scopeQuery(connectionIds)}`),
  },

  agentflow: {
    getAccount: () => request<AgentFlowAccountDTO>("/v1/agentflow/accounts/me"),
    activate: () => request<AgentFlowAccountDTO>("/v1/agentflow/accounts", { method: "POST" }),
    updateAccount: (body: UpdateAgentFlowAccountRequest) =>
      request<AgentFlowAccountDTO>("/v1/agentflow/accounts/me", {
        method: "PATCH",
        body: JSON.stringify(body),
      }),
    listFlows: () => request<{ flows: AgentFlowDTO[] }>("/v1/agentflow/flows"),
    getFlow: (id: string) => request<AgentFlowDTO>(`/v1/agentflow/flows/${id}`),
    createFlow: (body: CreateAgentFlowRequest) =>
      request<AgentFlowDTO>("/v1/agentflow/flows", {
        method: "POST",
        body: JSON.stringify(body),
      }),
    clarifyFlow: (body: ClarifyAgentFlowRequest) =>
      request<ClarifyAgentFlowResponse>("/v1/agentflow/flows/clarify", {
        method: "POST",
        body: JSON.stringify(body),
      }),
    generateFlow: (body: GenerateAgentFlowRequest) =>
      request<GenerateAgentFlowResponse>("/v1/agentflow/flows/generate", {
        method: "POST",
        body: JSON.stringify(body),
      }),
    generateExampleCustomer: (body: GenerateExampleCustomerRequest) =>
      request<GenerateExampleCustomerResponse>("/v1/agentflow/flows/example-customer-line", {
        method: "POST",
        body: JSON.stringify(body),
      }),

    // Account-scoped slot catalog (ADR 0030)
    listSlots: () =>
      request<ListAgentFlowSlotsResponse>("/v1/agentflow/slots").then((r) => r.slots),
    createSlot: (body: CreateAgentFlowSlotRequest) =>
      request<AgentFlowSlotDTO>("/v1/agentflow/slots", {
        method: "POST",
        body: JSON.stringify(body),
      }),
    updateSlot: (key: string, body: UpdateAgentFlowSlotRequest) =>
      request<AgentFlowSlotDTO>(`/v1/agentflow/slots/${encodeURIComponent(key)}`, {
        method: "PATCH",
        body: JSON.stringify(body),
      }),
    deleteSlot: (key: string) =>
      request<DeleteAgentFlowSlotResponse>(`/v1/agentflow/slots/${encodeURIComponent(key)}`, {
        method: "DELETE",
      }),
    updateFlow: (id: string, body: UpdateAgentFlowRequest) =>
      request<AgentFlowDTO>(`/v1/agentflow/flows/${id}`, {
        method: "PATCH",
        body: JSON.stringify(body),
      }),
    deleteFlow: (id: string) =>
      request<DeleteAgentFlowResponse>(`/v1/agentflow/flows/${id}`, {
        method: "DELETE",
      }),
    listInboundNumbers: () =>
      request<{ inboundNumbers: TwilioInboundNumberDTO[] }>("/v1/agentflow/inbound-numbers"),
    bindInboundNumber: (body: BindInboundNumberRequest) =>
      request<TwilioInboundNumberDTO>("/v1/agentflow/inbound-numbers", {
        method: "POST",
        body: JSON.stringify(body),
      }),

    // M1 — canvas authoring + sandbox + publish
    listTools: () => request<ListToolsResponse>("/v1/agentflow/tools"),
    listTemplates: () => request<ListTemplatesResponse>("/v1/agentflow/templates"),
    cloneTemplate: (body: CloneTemplateRequest) =>
      request<AgentFlowDTO>("/v1/agentflow/flows/from-template", {
        method: "POST",
        body: JSON.stringify(body),
      }),
    publishFlow: (id: string) =>
      request<PublishFlowResponse>(`/v1/agentflow/flows/${id}/publish`, { method: "POST" }),
    sandboxTurn: (id: string, body: SandboxTurnRequest) =>
      request<SandboxTurnResponse>(`/v1/agentflow/flows/${id}/sandbox/turn`, {
        method: "POST",
        body: JSON.stringify(body),
      }),

    // M2 — analytics
    analytics: (windowDays: AnalyticsWindowDays, flowId?: string) => {
      const params = new URLSearchParams({ windowDays: String(windowDays) });
      if (flowId) params.set("flowId", flowId);
      return request<AnalyticsResponse>(`/v1/agentflow/analytics?${params.toString()}`);
    },

    // M3 — compliance, replay, integrations
    recordConsent: (body: RecordConsentRequest) =>
      request<ConsentRecordDTO>("/v1/agentflow/consent", {
        method: "POST",
        body: JSON.stringify(body),
      }),
    consentSummary: () =>
      request<{ optedIn: number; optedOut: number; unknown: number }>(
        "/v1/agentflow/consent/summary",
      ),
    listConversations: (
      query: {
        channel?: ConversationChannel;
        flowId?: string;
        status?: "active" | "closed" | "handed_off";
        completed?: boolean;
        cursor?: string;
        limit?: number;
      } = {},
    ) => {
      const params = new URLSearchParams();
      if (query.channel) params.set("channel", query.channel);
      if (query.flowId) params.set("flowId", query.flowId);
      if (query.status) params.set("status", query.status);
      if (query.completed !== undefined) params.set("completed", String(query.completed));
      if (query.cursor) params.set("cursor", query.cursor);
      if (query.limit !== undefined) params.set("limit", String(query.limit));
      const qs = params.toString();
      return request<ListConversationsResponse>(`/v1/agentflow/conversations${qs ? `?${qs}` : ""}`);
    },
    getConversation: (id: string) =>
      request<ConversationReplayDTO>(`/v1/agentflow/conversations/${id}`),
    listPendingAppointments: (status?: "pending" | "confirmed" | "rejected") => {
      const qs = status ? `?status=${status}` : "";
      return request<{ pendingAppointments: PendingAppointmentDTO[] }>(
        `/v1/agentflow/pending-appointments${qs}`,
      );
    },
    transitionPendingAppointment: (id: string, status: "confirmed" | "rejected") =>
      request<PendingAppointmentDTO>(`/v1/agentflow/pending-appointments/${id}`, {
        method: "PATCH",
        body: JSON.stringify({ status }),
      }),
    listIntegrations: () =>
      request<{ integrations: IntegrationDTO[] }>("/v1/agentflow/integrations"),
    disconnectCalendar: () =>
      request<void>("/v1/agentflow/integrations/google-calendar", { method: "DELETE" }),
    /** Returns the *URL* to follow for the OAuth flow — caller does a full-page nav. */
    calendarAuthorizeUrl: () => apiUrl("/v1/agentflow/integrations/google-calendar/authorize"),
    // ---- channels (ADR 0016) ----
    listChannelBindings: (flowId: string) =>
      request<ListFlowChannelBindingsResponse>(`/v1/agentflow/flows/${flowId}/channels`).then(
        (r) => r.bindings as FlowChannelBindingDTO[],
      ),
    createChannelBinding: (flowId: string, body: CreateFlowChannelBindingRequest) =>
      request<CreateFlowChannelBindingResponse>(`/v1/agentflow/flows/${flowId}/channels`, {
        method: "POST",
        body: JSON.stringify(body),
      }),
    deleteChannelBinding: (bindingId: string) =>
      request<void>(`/v1/agentflow/channels/${bindingId}`, { method: "DELETE" }),
    rotateWebhookSecret: (bindingId: string) =>
      request<RotateWebhookSecretResponse>(`/v1/agentflow/channels/${bindingId}/rotate-secret`, {
        method: "POST",
      }),
    // ---- Twilio wizard (ADR 0017) ----
    listTwilioNumbers: (opts: {
      connectionId: string;
      query?: string;
      limit?: number;
      /** Annotate "already connected" for this channel only (ADR 0029). */
      channel?: "sms" | "voice";
    }) => {
      const params = new URLSearchParams();
      params.set("connectionId", opts.connectionId);
      if (opts.query) params.set("q", opts.query);
      if (opts.limit) params.set("limit", String(opts.limit));
      if (opts.channel) params.set("channel", opts.channel);
      return request<ListTwilioNumbersResponse>(
        `/v1/agentflow/twilio/numbers?${params.toString()}`,
      ).then((r) => r.numbers as TwilioNumberDTO[]);
    },
    connectTwilioNumber: (flowId: string, body: ConnectTwilioNumberRequest) =>
      request<ConnectTwilioNumberResponse>(`/v1/agentflow/flows/${flowId}/channels/twilio`, {
        method: "POST",
        body: JSON.stringify(body),
      }),
    // ---- Voice wizard (ADR 0029) — reuses the number-connect body shape ----
    connectTwilioVoiceNumber: (flowId: string, body: ConnectTwilioNumberRequest) =>
      request<ConnectTwilioVoiceNumberResponse>(
        `/v1/agentflow/flows/${flowId}/channels/twilio/voice`,
        { method: "POST", body: JSON.stringify(body) },
      ),
    // ---- WhatsApp / RCS channel senders ----
    listTwilioChannelSenders: (opts: { connectionId: string; channel: "whatsapp" | "rcs" }) => {
      const params = new URLSearchParams();
      params.set("connectionId", opts.connectionId);
      params.set("channel", opts.channel);
      return request<ListTwilioChannelSendersResponse>(
        `/v1/agentflow/twilio/channel-senders?${params.toString()}`,
      ).then((r) => r.senders as TwilioChannelSenderDTO[]);
    },
    connectChannelSender: (flowId: string, body: ConnectChannelSenderRequest) =>
      request<ConnectChannelSenderResponse>(
        `/v1/agentflow/flows/${flowId}/channels/twilio/channel-sender`,
        { method: "POST", body: JSON.stringify(body) },
      ),
    // ---- WhatsApp templates ----
    getWhatsAppTemplate: (flowId: string) =>
      request<GetWhatsAppTemplateResponse>(`/v1/agentflow/flows/${flowId}/whatsapp-template`),
    registerWhatsAppTemplate: (flowId: string, body: RegisterWhatsAppTemplateRequest) =>
      request<WhatsAppTemplateDTO>(`/v1/agentflow/flows/${flowId}/whatsapp-template`, {
        method: "POST",
        body: JSON.stringify(body),
      }),
    refreshWhatsAppTemplate: (flowId: string) =>
      request<WhatsAppTemplateDTO>(`/v1/agentflow/flows/${flowId}/whatsapp-template/refresh`, {
        method: "POST",
      }),
    listApprovedWhatsAppTemplates: (flowId: string) =>
      request<ListApprovedWhatsAppTemplatesResponse>(
        `/v1/agentflow/flows/${flowId}/whatsapp-templates/approved`,
      ),
    // ---- Outbound initiation (ADR 0018) ----
    initiateOutbound: (flowId: string, body: InitiateOutboundRequest) =>
      request<InitiateOutboundResponse>(`/v1/agentflow/flows/${flowId}/conversations`, {
        method: "POST",
        body: JSON.stringify(body),
      }),
    // ---- Knowledge base (ADR 0021) ----
    listKnowledgeEntries: (
      opts: {
        status?: "active" | "archived";
        type?: "qa" | "doc_chunk";
        query?: string;
        tags?: string[];
        cursor?: string;
        limit?: number;
      } = {},
    ) => {
      const params = new URLSearchParams();
      if (opts.status) params.set("status", opts.status);
      if (opts.type) params.set("type", opts.type);
      if (opts.query) params.set("q", opts.query);
      if (opts.tags) for (const t of opts.tags) params.append("tag", t);
      if (opts.cursor) params.set("cursor", opts.cursor);
      if (opts.limit) params.set("limit", String(opts.limit));
      const qs = params.toString();
      return request<ListKnowledgeEntriesResponse>(
        `/v1/agentflow/knowledge-base${qs ? `?${qs}` : ""}`,
      );
    },
    listKnowledgeTags: () =>
      request<ListKnowledgeTagsResponse>("/v1/agentflow/knowledge-base/tags").then((r) => r.tags),
    getKnowledgeEntry: (id: string) =>
      request<KnowledgeEntryDTO>(`/v1/agentflow/knowledge-base/${id}`),
    createKnowledgeEntry: (body: CreateKnowledgeEntryRequest) =>
      request<KnowledgeEntryDTO>("/v1/agentflow/knowledge-base", {
        method: "POST",
        body: JSON.stringify(body),
      }),
    updateKnowledgeEntry: (id: string, body: UpdateKnowledgeEntryRequest) =>
      request<KnowledgeEntryDTO>(`/v1/agentflow/knowledge-base/${id}`, {
        method: "PATCH",
        body: JSON.stringify(body),
      }),
    deleteKnowledgeEntry: (id: string) =>
      request<void>(`/v1/agentflow/knowledge-base/${id}`, { method: "DELETE" }),
    // ---- Knowledge documents (pdf/docx ingestion) ----
    listKnowledgeDocuments: () =>
      request<ListKnowledgeDocumentsResponse>("/v1/agentflow/knowledge-base/documents").then(
        (r) => r.documents,
      ),
    uploadKnowledgeDocument: (file: File, tags?: string[]) => {
      const params = new URLSearchParams({ filename: file.name });
      if (tags && tags.length > 0) params.set("tags", tags.join(","));
      return request<UploadKnowledgeDocumentResponse>(
        `/v1/agentflow/knowledge-base/documents?${params.toString()}`,
        {
          method: "POST",
          body: file,
          headers: { "content-type": "application/octet-stream" },
        },
      ).then((r) => r.document);
    },
    updateKnowledgeDocument: (id: string, tags: string[]) =>
      request<UpdateKnowledgeDocumentResponse>(`/v1/agentflow/knowledge-base/documents/${id}`, {
        method: "PATCH",
        body: JSON.stringify({ tags }),
      }).then((r) => r.document),
    deleteKnowledgeDocument: (id: string) =>
      request<void>(`/v1/agentflow/knowledge-base/documents/${id}`, { method: "DELETE" }),
    /** Returns the stored original as a Blob. Caller triggers the save. */
    downloadKnowledgeDocument: async (id: string): Promise<Blob> => {
      const headers = new Headers();
      const token = getToken();
      if (token) headers.set("authorization", `Bearer ${token}`);
      const res = await fetch(`${BASE_URL}/v1/agentflow/knowledge-base/documents/${id}/download`, {
        headers,
      });
      if (!res.ok) {
        const json = (await res.json().catch(() => undefined)) as ApiError | undefined;
        throw new ApiClientError(
          res.status,
          json?.error?.code ?? "download_failed",
          json?.error?.message ?? `HTTP ${res.status}`,
        );
      }
      return res.blob();
    },
    // ---- handoff (ADR 0015) ----
    getHandoff: () => request<AgentFlowHandoffConfigDTO>("/v1/agentflow/handoff"),
    updateHandoff: (body: UpdateAgentFlowHandoffRequest) =>
      request<AgentFlowHandoffConfigDTO>("/v1/agentflow/handoff", {
        method: "PUT",
        body: JSON.stringify(body),
      }),
    // ---- action-event webhook ----
    getActionWebhook: () =>
      request<AgentFlowActionWebhookConfigDTO>("/v1/agentflow/action-webhook"),
    updateActionWebhook: (body: UpdateAgentFlowActionWebhookRequest) =>
      request<AgentFlowActionWebhookConfigDTO>("/v1/agentflow/action-webhook", {
        method: "PUT",
        body: JSON.stringify(body),
      }),
    sendTestActionEvent: () =>
      request<SendTestActionEventResponse>("/v1/agentflow/action-webhook/test", {
        method: "POST",
      }),
  },

  tenants: {
    updateMe: (body: UpdateTenantBrandingRequest) =>
      request<Tenant>("/v1/tenants/me", {
        method: "PATCH",
        body: JSON.stringify(body),
      }),
  },

  errorLogs: {
    list: (connectionIds?: string[], query: ListErrorLogsQuery = {}) => {
      const params = new URLSearchParams();
      if (connectionIds && connectionIds.length > 0) {
        params.set("connectionIds", connectionIds.join(","));
      }
      if (query.logLevel) params.set("logLevel", query.logLevel);
      if (query.errorCode !== undefined) params.set("errorCode", String(query.errorCode));
      if (query.search) params.set("search", query.search);
      if (query.limit !== undefined) params.set("limit", String(query.limit));
      if (query.cursor) params.set("cursor", query.cursor);
      const qs = params.toString();
      return request<ListErrorLogsResponse>(`/v1/error-logs${qs ? `?${qs}` : ""}`);
    },
    summary: (connectionIds?: string[]) =>
      request<ErrorLogsSummary>(`/v1/error-logs/summary${scopeQuery(connectionIds)}`),
  },
};

/**
 * Build a `?connectionIds=…` query suffix from the active scope. Empty array
 * means "all" — we omit the param so the backend treats it as unscoped.
 */
function scopeQuery(connectionIds?: string[]): string {
  if (!connectionIds || connectionIds.length === 0) return "";
  return `?connectionIds=${connectionIds.join(",")}`;
}

function scopeSuffix(connectionIds?: string[]): string {
  if (!connectionIds || connectionIds.length === 0) return "";
  return `&connectionIds=${connectionIds.join(",")}`;
}
